import { SharedForm } from '@components';
import { InitialVehicle } from '@services';
import { useStore } from '@stores';
import { Rules } from '@utils';
import { Alert, Col, DatePicker, Form, FormInstance, Input, Row } from 'antd';
import { observer } from 'mobx-react';
import React from 'react';

interface IVehicleFormProps {
  form: FormInstance<any>;
  onFinish: (values: any) => Promise<void>;
  onCancel: (value: boolean) => void;
}

export const VehicleForm: React.FC<IVehicleFormProps> = observer(({ form, onFinish, onCancel }) => {
  const { vehicleStore } = useStore();
  const { loading } = vehicleStore;
  return (
    <SharedForm
      form={form}
      loading={loading}
      name="createOrUpdateVehicle"
      layout="vertical"
      submitTitle={'Save vehicle'}
      initialValues={InitialVehicle}
      onFinish={onFinish}
      onCancel={onCancel}
    >
      <Row>
        <Col span={24}>
          <Alert
            message="We couldn’t find the vehicle you were searching for. Please create it manually."
            type="info"
            showIcon
          />
        </Col>
      </Row>
      <br />
      <Row gutter={8} justify="space-between">
        <Form.Item name="id" hidden>
          <Input />
        </Form.Item>
        <Col span={8}>
          <Form.Item required rules={Rules.required} name="combinedMake" label={'Make'}>
            <Input placeholder={'Make'} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item required rules={Rules.required} name="combinedModel" label={'Model'}>
            <Input placeholder={'Model'} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            required
            rules={[
              { required: true, message: 'VIN is required' },
              {
                pattern: /^[A-HJ-NPR-Za-hj-npr-z0-9]{17}$/,
                message: 'Invalid VIN format',
              },
            ]}
            name="combinedVin"
            label={'Vin'}
          >
            <Input
              placeholder="VIN"
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                form.setFieldsValue({ combinedVin: value });
              }}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={8} justify="space-between">
        <Col span={8}>
          <Form.Item label={'Year of manufacture'} name="dvlaYearOfManufacture">
            <Input type="number" min={0} placeholder={'Year of manufacture'} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={'Engine size'} name="combinedEngineCapacity">
            <Input type="number" min={0} placeholder={'Engine size'} />
          </Form.Item>
        </Col>
        {form.getFieldValue('exemptFromMot') ? (
          <Col span={8}>
            <Form.Item label={'MOT expiry date'}>
              <Input disabled placeholder={'Select date'} value={'Exempt from MOT'} />
            </Form.Item>
          </Col>
        ) : (
          <Col span={8}>
            <Form.Item name="motExpiryDate" label={'MOT expiry date'}>
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        )}
      </Row>
      <Row gutter={8} justify="space-between">
        <Col span={8}>
          <Form.Item
            required
            name="combinedTransmission"
            label={'Gear type'}
            rules={Rules.required}
          >
            <Input placeholder={'Gear type'} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="vehicleCategoryDescription" label={'Vehicle type'}>
            <Input placeholder={'Vehicle type'} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="euroStatus" label={'Euro status'}>
            <Input placeholder={'Euro Status'} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={8} justify="space-between">
        <Col span={8}>
          <Form.Item name="modelSeries" label={'Model series'}>
            <Input placeholder={'Model series'} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="dateFirstRegistered" label={'First registration'}>
            <DatePicker
              showTime={false}
              style={{ width: '100%' }}
              placeholder={'First registration'}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="colour" label={'Colour'}>
            <Input placeholder={'Colour'} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={8} justify="space-between">
        <Col span={8}>
          <Form.Item name="wheelplan" label={'Wheelbase type'}>
            <Input placeholder={'Wheelbase type'} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="driveType" label={'Drive type'}>
            <Input placeholder={'Drive type'} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="engineNumber" label={'Engine number'}>
            <Input placeholder={'Engine number'} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={8} justify="space-between">
        <Col span={8}>
          <Form.Item name="grossWeightKg" label={'Gross weight'}>
            <Input type="number" min={0} placeholder={'Gross weight'} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="startDateOfCurrentKeeper" label={'Current keeper'}>
            <DatePicker style={{ width: '100%' }} placeholder={'Current keeper'} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item required name="combinedFuelType" rules={Rules.required} label={'Fuel type'}>
            <Input placeholder={'Fuel type'} />
          </Form.Item>
        </Col>
      </Row>
    </SharedForm>
  );
});
